<script setup>
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { SEM_ACESSO, escolhaDoGrupo, mesmoGrupo, naoSeDa } from '../catalogo';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

// ACESSO-01 (L9a): o seletor do grupo de um módulo, como no quadro 3 do mock v3. Um menu com seções: os grupos da
// Powerbizz ("Sem acesso" primeiro, sem os que só os modelos usam), os da conta e, no fim, "Personalizar escopos" e
// "Criar grupo". Cada grupo diz o que libera numa segunda linha cinza. O grupo com escopo que não se dá (R6: não se
// concede, ou falta a quem edita) fica desabilitado, com a nota. Os títulos das seções ficam em caixa de frase, como no
// mock aprovado: a hierarquia do OS é de peso e cor, e as maiúsculas espaçadas gritavam mais que os grupos. No modelo,
// que é só leitura, o grupo é texto: o botão desabilitado ficava apagado (4,2:1 no tema claro) e parecia quebrado.
const props = defineProps({
  modulo: { type: Object, required: true },
  // Os do módulo, no catálogo.
  grupos: { type: Array, required: true },
  escopos: { type: Array, required: true },
  escolha: { type: Object, required: true },
  somenteLeitura: { type: Boolean, default: false },
});

const emit = defineEmits(['escolher', 'personalizar', 'criarGrupo']);

const { t } = useI18n();
const { pode } = useAcesso();

const aberto = ref(false);
const caixa = useTemplateRef('caixa');
const gatilho = useTemplateRef('gatilho');
onClickOutside(caixa, () => {
  aberto.value = false;
});

const fechar = () => {
  aberto.value = false;
  gatilho.value?.focus();
};

const atual = computed(() =>
  props.grupos.find(grupo => mesmoGrupo(grupo, props.escolha))
);

const escoposQueNaoSeDao = computed(
  () =>
    new Set(
      props.escopos
        .filter(escopo => naoSeDa(escopo, pode))
        .map(escopo => escopo.nome)
    )
);

// O grupo da conta não tem frase: a linha diz quantos escopos ele tem.
const opcao = grupo => {
  const bloqueado = grupo.escopos.some(escopo =>
    escoposQueNaoSeDao.value.has(escopo)
  );
  const n = grupo.escopos.length;
  return {
    label: grupo.rotulo,
    description: bloqueado
      ? t('PAPEIS.NAO_CONCEDIVEL')
      : grupo.frase || t('PAPEIS.SELETOR.ESCOPOS_DO_GRUPO', { n }, n),
    value: grupo.origem === 'conta' ? `conta:${grupo.id}` : grupo.chave,
    action: 'escolher',
    grupo,
    isSelected: mesmoGrupo(grupo, props.escolha),
    disabled: bloqueado,
  };
};

const secoes = computed(() => {
  const daPowerbizz = props.grupos
    .filter(grupo => grupo.origem === 'powerbizz' && !grupo.so_modelo)
    .sort((a, b) => (b.chave === SEM_ACESSO) - (a.chave === SEM_ACESSO));
  const daConta = props.grupos.filter(grupo => grupo.origem === 'conta');

  return [
    {
      title: t('PAPEIS.SELETOR.DA_POWERBIZZ'),
      items: daPowerbizz.map(opcao),
    },
    ...(daConta.length
      ? [{ title: t('PAPEIS.SELETOR.DA_CONTA'), items: daConta.map(opcao) }]
      : []),
    {
      items: [
        {
          label: t('PAPEIS.SELETOR.PERSONALIZAR'),
          value: 'personalizar',
          action: 'personalizar',
          icon: 'i-lucide-settings-2',
        },
        {
          label: t('PAPEIS.SELETOR.CRIAR_GRUPO'),
          value: 'criar_grupo',
          action: 'criarGrupo',
          icon: 'i-lucide-plus',
        },
      ],
    },
  ];
});

// O item escolhido some com o menu: o foco volta ao seletor, para quem usa o teclado não cair no começo da página.
// "Personalizar escopos" leva o foco aos escopos (`LinhaDoModulo`), e "Criar grupo", ao Dialog.
const agir = ({ action, grupo }) => {
  aberto.value = false;
  if (action === 'escolher') {
    emit('escolher', escolhaDoGrupo(grupo));
    gatilho.value?.focus();
  }
  if (action === 'personalizar') emit('personalizar');
  if (action === 'criarGrupo') emit('criarGrupo');
};
</script>

<template>
  <div
    v-if="modulo.travado"
    class="flex items-center justify-between w-full h-8 gap-2 px-2.5 text-sm border border-dashed rounded-lg sm:w-44 shrink-0 border-n-strong text-n-slate-11"
    data-bloqueado
  >
    {{ t('PAPEIS.EDITOR.BLOQUEADO') }}
    <span class="i-lucide-lock size-3.5 shrink-0" aria-hidden="true" />
  </div>
  <p
    v-else-if="somenteLeitura"
    class="w-full px-2.5 mb-0 text-sm truncate sm:w-44 shrink-0 text-n-slate-12"
    :data-seletor="modulo.chave"
  >
    {{ atual?.rotulo }}
  </p>
  <div
    v-else
    ref="caixa"
    class="relative w-full sm:w-44 shrink-0"
    @keydown.esc="fechar"
  >
    <button
      ref="gatilho"
      type="button"
      class="flex items-center justify-between w-full h-8 gap-2 px-2.5 text-sm rounded-lg text-start outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 transition-colors hover:outline-n-slate-6 focus-visible:outline-2 focus-visible:outline-n-brand"
      :aria-expanded="aberto"
      :aria-label="
        t('PAPEIS.SELETOR.ROTULO', {
          modulo: modulo.rotulo,
          grupo: atual?.rotulo || '',
        })
      "
      :data-seletor="modulo.chave"
      @click="aberto = !aberto"
    >
      <span class="truncate">{{ atual?.rotulo }}</span>
      <span
        class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-11"
        aria-hidden="true"
      />
    </button>
    <DropdownMenu
      v-if="aberto"
      :menu-sections="secoes"
      section-title-class="normal-case"
      class="right-0 mt-1 top-full w-80 max-w-[calc(100vw-2rem)]"
      :data-opcoes="modulo.chave"
      @action="agir"
    >
      <template #trailing-icon="{ item }">
        <template v-if="item.isSelected">
          <span
            class="ml-auto i-lucide-check size-3.5 shrink-0 text-pz-brand-on-tint"
            aria-hidden="true"
          />
          <span class="sr-only" data-escolhido>
            {{ t('PAPEIS.SELETOR.ESCOLHIDO') }}
          </span>
        </template>
      </template>
    </DropdownMenu>
  </div>
</template>
